import { vars } from '@etg/wings';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { css, etiColors } from '@eti/styles';
import type { SortType } from '@eti/schema-types';
import { useTranslation } from '@eti/providers';
import type { Dispatch, SetStateAction, FocusEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { price as priceUtil } from '@eti/utils';
import { observeClickOutside } from 'react-ref-observe';
import QuickSortButton from './QuickSortButton';
import { getLongestQuickSortOption, getRecommendedQuickSortOption } from './utils';
import { quickSortPricesType } from './quickSortConstants';

const quickSortDropdownButtonStyles = css`
  border-radius: 0 ${vars.card.borderRadius} ${vars.card.borderRadius} 0;
  border-right: 0;
  height: 100%;

  [dir='rtl'] & {
    border-radius: ${vars.card.borderRadius} 0 0 ${vars.card.borderRadius};
    border-right: 1px solid ${vars.colors.divider};
  }
`;

const dropdownQuickSortButtonWrapperStyles = css`
  flex-basis: 100%;
  position: relative;
`;

const caretIconStyles = (isDropdownOpen: boolean) => css`
  background-color: ${isDropdownOpen ? vars.colors.inputs.focus : 'transparent'};
`;

const dropdownWrapperStyles = css`
  background: ${etiColors.white};
  border-radius: 3px;
  box-shadow: 0 1px 3px 0 #0000001a;
  margin-block-start: 4px;
  position: absolute;
  width: 100%;
  z-index: 10;
`;

const quickSortDropdownContentButtonStyles = (isRecommended: boolean) => css`
  border-bottom: ${isRecommended ? '4px solid transparent' : `1px solid ${vars.colors.divider}`};
  border-radius: 0;
  border-right: none;
  width: 100%;
`;

type QuickShortDropdownOption = {
  label: string;
  time?: string | null;
  price: string | null;
  value: keyof typeof quickSortPricesType | null;
  desc?: string;
};

interface QuickSortDropdownProps {
  isDropdownOpen: boolean | null;
  onButtonClick: (value: keyof typeof quickSortPricesType) => void;
  selectedSortType: SortType;
  setIsDropdownOpen: Dispatch<SetStateAction<boolean | null>>;
  sortPrices: {
    bestValue: number;
    carrierPromo: number;
    cheapTrip: number;
    recommendation: number;
    shortTrip: number;
  };
}

const QuickSortDropdown = ({
  isDropdownOpen,
  onButtonClick,
  selectedSortType,
  setIsDropdownOpen,
  sortPrices,
}: QuickSortDropdownProps) => {
  const { t } = useTranslation();
  const dropdownButtonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownContainerRef = useRef<HTMLDivElement | null>(null);
  const [selectedDropdownOption, setSelectedDropdownOption] = useState<QuickShortDropdownOption>({
    label: t('Result.QuickSort.MoreOptions.Button.Label'),
    time: null,
    price: null,
    value: null,
  });
  const [shouldFocusDropdown, setShouldFocusDropdown] = useState(false);

  const {
    desc: recommendedDesc,
    labelKey: recommendedLabelKey,
    value: recommendedValue,
  } = getRecommendedQuickSortOption(t);

  const {
    desc: longestDesc,
    labelKey: longestLabelKey,
    value: longestValue,
  } = getLongestQuickSortOption(t);

  useEffect(() => {
    if (shouldFocusDropdown) {
      dropdownButtonRef.current?.focus();
      setShouldFocusDropdown(false);
    }
  }, [shouldFocusDropdown]);

  const recommendedQuickSortPriceFormated = priceUtil.formatPrice(
    sortPrices[quickSortPricesType[recommendedValue] as keyof typeof sortPrices],
  );

  const longestQuickSortPriceFormated = priceUtil.formatPrice(
    sortPrices[quickSortPricesType[longestValue] as keyof typeof sortPrices],
  );

  const onDropdownContentButtonClick = (
    label: string,
    value: keyof typeof quickSortPricesType,
    price: any,
    desc?: string,
  ) => {
    onButtonClick(value);
    setSelectedDropdownOption({
      label,
      value,
      price,
      desc,
    });
    setIsDropdownOpen(false);
    setShouldFocusDropdown(true);
    localStorage.setItem('isQuickBarDropDownSelected', 'true');
  };

  const onDropdownButtonClick = () => {
    if (selectedDropdownOption.value) {
      onButtonClick(selectedDropdownOption.value);
    }
    setIsDropdownOpen((prev) => (prev === null ? true : !prev));
  };

  const handleBlur = (e: FocusEvent<HTMLElement>) => {
    if (
      e.relatedTarget instanceof Node &&
      !dropdownContainerRef.current?.contains(e.relatedTarget)
    ) {
      setIsDropdownOpen(false);
    }
  };

  observeClickOutside([dropdownContainerRef], () => setIsDropdownOpen(false));

  const dropdownAriaLabel = isDropdownOpen
    ? `${selectedDropdownOption.label}, ${t(
        'Result.QuickSort.MoreOptions.CloseDropDown.AriaLabel',
      )}`
    : `${selectedDropdownOption.label}, ${t(
        'Result.QuickSort.MoreOptions.OpenDropDown.AriaLabel',
      )}`;

  return (
    <div className={dropdownQuickSortButtonWrapperStyles} ref={dropdownContainerRef}>
      <QuickSortButton
        aria-label={dropdownAriaLabel}
        className={quickSortDropdownButtonStyles}
        data-testid="quicksort-dropdown-button"
        desc={selectedDropdownOption.desc}
        formattedPrice={
          selectedDropdownOption.price && selectedDropdownOption.value
            ? priceUtil.formatPrice(
                sortPrices[
                  quickSortPricesType[selectedDropdownOption.value] as keyof typeof sortPrices
                ],
              )
            : undefined
        }
        isSelected={selectedSortType === selectedDropdownOption.value}
        label={selectedDropdownOption.label}
        onClick={() => onDropdownButtonClick()}
        ref={dropdownButtonRef}
        rightSection={
          isDropdownOpen ? (
            <CaretUp
              aria-hidden
              className={caretIconStyles(isDropdownOpen)}
              data-testid="quicksort-dropdown-icon-caret-up"
              size={20}
            />
          ) : (
            <CaretDown
              aria-hidden
              className={caretIconStyles(isDropdownOpen ?? false)}
              data-testid="quicksort-dropdown-icon-caret-down"
              size={20}
            />
          )
        }
        tooltipPlacement={isDropdownOpen ? 'right' : 'bottom'}
        type={selectedDropdownOption.value ?? ''}
      />
      {isDropdownOpen && (
        <div
          className={dropdownWrapperStyles}
          data-testid="quicksort-dropdown-content-wrapper"
          onBlur={handleBlur}
        >
          {selectedDropdownOption.value !== longestValue && (
            <QuickSortButton
              className={quickSortDropdownContentButtonStyles(false)}
              desc={longestDesc}
              formattedPrice={longestQuickSortPriceFormated}
              isSelected={selectedSortType === longestValue}
              label={longestLabelKey}
              onClick={() =>
                onDropdownContentButtonClick(
                  longestLabelKey,
                  longestValue,
                  longestQuickSortPriceFormated,
                  longestDesc,
                )
              }
              type={longestValue}
            />
          )}
          {selectedDropdownOption.value !== recommendedValue && (
            <QuickSortButton
              className={quickSortDropdownContentButtonStyles(true)}
              desc={recommendedDesc}
              formattedPrice={recommendedQuickSortPriceFormated}
              isSelected={selectedSortType === recommendedValue}
              label={recommendedLabelKey}
              onClick={() =>
                onDropdownContentButtonClick(
                  recommendedLabelKey,
                  recommendedValue,
                  recommendedQuickSortPriceFormated,
                  recommendedDesc,
                )
              }
              tooltipPlacement="left"
              type={recommendedValue}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default QuickSortDropdown;
